import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  LayoutDashboard, 
  Scissors, 
  Star, 
  BookOpen, 
  FileText, 
  LogOut, 
  Menu, 
  X,
  Home,
  MapPin,
  Settings,
  ClipboardList,
  Mail,
  Wrench
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_88d1f69c-9fa6-4970-b193-9f501d9a2094/artifacts/igeyaxn2_Icon-Jardily.png";

const AdminLayout = ({ children, title }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { name: 'Dashboard', icon: LayoutDashboard, href: '/admin' },
    { name: 'Services', icon: Scissors, href: '/admin/services' },
    { name: 'Avis clients', icon: Star, href: '/admin/reviews' },
    { name: 'Blog', icon: BookOpen, href: '/admin/blog' },
    { name: 'Demandes de devis', icon: ClipboardList, href: '/admin/quotes' },
    { name: 'Pages', icon: FileText, href: '/admin/pages' },
    { name: 'Pages SEO Villes', icon: MapPin, href: '/admin/city-pages' },
    { name: 'Contenu du site', icon: Settings, href: '/admin/site-content' },
    { name: 'Emails & SMTP', icon: Mail, href: '/admin/emails' },
    { name: 'Paramètres', icon: Wrench, href: '/admin/settings' },
  ];

  const isActive = (href) => location.pathname === href;

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-stone-200 z-40 flex items-center justify-between px-4">
        <button 
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 hover:bg-stone-100 rounded-lg"
          data-testid="mobile-sidebar-toggle"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <img src={LOGO_URL} alt="Jardily" className="h-8" />
        <div className="w-10"></div>
      </div>

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full w-64 bg-stone-900 text-white z-50 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="admin-sidebar"
      >
        <div className="p-6">
          <Link to="/admin" className="flex items-center gap-3 mb-8">
            <img src={LOGO_URL} alt="Jardily" className="h-10 w-10" />
            <div>
              <h2 className="font-heading font-bold text-lg">Jardily</h2>
              <p className="text-stone-400 text-xs">Back Office</p>
            </div>
          </Link>

          <nav className="space-y-1">
            {menuItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  isActive(item.href)
                    ? 'bg-emerald-600 text-white'
                    : 'text-stone-300 hover:bg-stone-800 hover:text-white'
                }`}
                data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-5 h-5" />
                {item.name}
              </Link>
            ))}
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-stone-800">
          <Link 
            to="/" 
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-stone-300 hover:bg-stone-800 hover:text-white transition-colors mb-2"
          >
            <Home className="w-5 h-5" />
            Voir le site
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            Déconnexion
          </button>
          {user && (
            <p className="text-stone-500 text-xs mt-4 px-4">
              Connecté: {user.email}
            </p>
          )}
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          <div className="mb-8">
            <h1 className="font-heading font-bold text-2xl lg:text-3xl text-stone-900">
              {title}
            </h1>
          </div>
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
